/**
 * auoMarks.ts — AUO (Activity / Utility / Object) marking on the grid
 * (Slice 5 / "Stage 5" — the AUO confirm-loop).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (reuse, don't reinvent — owner instruction)
 * ─────────────────────────────────────────────────────────────────────────
 *  The engine cannot see the floor plan, so an AUO's zone must be MARKED on the
 *  grid, not typed. This REUSES the existing internal-door marking interaction:
 *  an AUO (Toilet, Kitchen, Bedroom, Borewell, Staircase …) sits INSIDE the
 *  footprint, so staff click a point inside the boundary and the 16-zone is
 *  auto-derived — exactly like an internal door's pada is derived. The ONLY
 *  difference: an internal door reads its 32-PADA by direction
 *  (`padaForInteriorPoint`); an AUO reads its 16-ZONE by point-in-polygon
 *  (`zoneAtPoint` — the sibling classifier vastuGrid already exposes, built for
 *  exactly "the click-AUO→zone step"). NO new zone-derivation, NO new marking
 *  gesture, NO second geometry copy — the same grid math the report uses.
 *
 *  An AUO MARK carries:
 *   · `point {x,y}` — the click location in IMAGE-PIXEL space (OQ-C), so the
 *     marker re-renders on reload and the zone re-derives if the grid moves.
 *   · `zone` — DERIVED via `zoneAtPoint` (16-zone code, e.g. 'NE'). The trust
 *     signal that replaces blind typing. '' when the point is off-grid.
 *   · `name` — the AUO name (from the per-project-type palette, or inline-added).
 *     Must NORMALISE via the engine's `normaliseAuoName` to be analysed — the UI
 *     warns when it won't (the engine silently skips unknowns at compose).
 *   · `type` — Activity | Utility | Object (a label only; never affects the
 *     disposition, which is matrix(name × zone)).
 *   · `status` — 'proposed' (engine-suggested / freshly marked) | 'confirmed'
 *     (staff accepted) | 'rejected' (staff declined, logged not compiled).
 *   · `suggestion?` — the engine's {disposition, severity, reason} from the
 *     `suggestAuoFixtures` callable, surfaced in the confirm card.
 *   · `rejected_by`/`rejected_at` — set on reject (training signal).
 *
 *  PERSIST CONTRACT (verified against the engine read site step2_auo.js:131,135):
 *   `answers.floors[i].fixtures[] = { name, zone, type, status, source,
 *     suggestion?, rejected_by?, rejected_at? }`. The engine reads `fx.name` +
 *     `fx.zone` and ignores the rest; the confirmed-only COMPOSE SEAM
 *     (`confirmedFixturesOnly` in loadInputs) strips status!=='confirmed' AND
 *     the status/suggestion/source fields before any pipeline step — so the
 *     engine sees the clean `{name, zone, type}` it always did. The fixtures ride
 *     INSIDE each floor object → they round-trip through the SAME `saveFloors`
 *     one-owner-of-the-floors-array path as boundary/doors (a separate fixtures
 *     write would be clobbered by the next floors save — the Slice-2 invariant).
 *
 *  This module is PURE (no Firebase, no DOM) so it unit-tests via _tsload. The
 *  zone derivation reuses `zoneAtPoint` from vastuGrid (one classifier).
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  zoneAtPoint,
  zonesForRegion,
  type AuoRegion,
  type GridResult,
  type Pt,
} from './vastuGrid';
import type { FixtureInput, FloorInput } from './staffIntake';

/* ───────────────────────── AUO shapes (Slice 5 redesign 2026-06-08) ─────────────────────────
 *  An AUO is a SHAPE, not a point: a bed / cabinet / counter placed in a room can
 *  fall into MULTIPLE 16-zones (owner ruling). A mark carries a `region`
 *  (square/rectangle with rotation, or circle) and the DERIVED set of zones it
 *  overlaps (`zones[]`), via `zonesForRegion` — the region case of the SAME
 *  `zoneAtPoint` classifier the door multi-pada coverage reuses. A legacy
 *  point-AUO is a degenerate rect; `zone` (singular) stays as `zones[0]` for
 *  backward compatibility with the single-zone persist + callable path.
 */

/** Default half-extent (px) of a freshly-placed AUO rect — a small square the
 *  staff then resize. Image-pixel space. */
export const DEFAULT_AUO_HALF_PX = 40;

/** Build the default region for a fresh click point (a small upright square). */
export function defaultRegionAt(pt: Pt): AuoRegion {
  return {
    kind: 'rect',
    cx: Math.round(pt.x),
    cy: Math.round(pt.y),
    hw: DEFAULT_AUO_HALF_PX,
    hh: DEFAULT_AUO_HALF_PX,
    rotation: 0,
  };
}

/** The centre point of any region (the marker anchor + legacy `point`). */
export function regionCentre(reg: AuoRegion): Pt {
  return { x: reg.cx, y: reg.cy };
}

/** The AUO categories (label only — never affects the disposition; resolution is
 *  by NAME via the canonical alias map). 'Colour' was added 2026-06-09 for the
 *  owner-dictated colour AUO family (Yellow/Red/Green/Blue/White colour) — a colour
 *  is marked on a REGION exactly like any AUO and resolves one ruling per covered
 *  zone; no new marker type. */
export const AUO_TYPES = ['Activity', 'Utility', 'Object', 'Colour'] as const;
export type AuoType = (typeof AUO_TYPES)[number];

export function isAuoType(s: unknown): s is AuoType {
  return typeof s === 'string' && (AUO_TYPES as readonly string[]).includes(s);
}

/** An AUO's review status. */
export const AUO_STATUSES = ['proposed', 'confirmed', 'rejected'] as const;
export type AuoStatus = (typeof AUO_STATUSES)[number];

export function isAuoStatus(s: unknown): s is AuoStatus {
  return typeof s === 'string' && (AUO_STATUSES as readonly string[]).includes(s);
}

/** The engine's per-(fixture × zone) reasoning (from the suggestAuoFixtures
 *  callable). One AUO shape covering N zones produces N of these. As of the
 *  canonical-table rewire (2026-06-08) it carries the owner's REAL XLS ruling:
 *  the verbatim effect text, the remedy, and the corrective colour. */
export interface AuoSuggestion {
  /** the 16-zone this ruling is FOR (a multi-zone AUO has one per zone). */
  zone?: string;
  /** the CANONICAL AUO key the engine resolved this name to (a real key in the
   *  dispositions table), or null if unrecognised. Needed to address the override
   *  doc id `{normalised}__{zone}` when the owner corrects this cell. */
  normalised?: string | null;
  /** matrix tag: 'OK' | 'TOL' | 'NO' | null (null = db-miss / unrecognised). */
  tag?: string | null;
  /** disposition LABEL: 'favourable' | 'neutral' | 'caution' | 'critical' |
   *  'unrated' (db-miss) | null (unrecognised). Was the tag pre-rewire. */
  disposition: string | null;
  /** the owner's VERBATIM effect text for this (AUO × zone), or null. */
  effect?: string | null;
  /** the remedy / management text, or null (XLS carries none yet for AUOs). */
  management?: string | null;
  /** the owner's corrective colour for this cell, or null. */
  corrective_colour?: string | null;
  /** the owner's corrective metal for this cell, or null. */
  corrective_metal?: string | null;
  /** 'vastu_xls' | 'db-miss' | null (unrecognised). */
  source?: string | null;
  /** 0..3. */
  severity: number;
  /** 'critical' | 'remedy' | 'ok' | 'unspecified'. */
  severity_label: string;
  /** staff-facing sentence. */
  reason: string;
  /** false → the name won't normalise; the engine would skip it (UI warns). */
  recognised: boolean;
  /** when `source==='override'`, which layer the correction came from:
   *  'project' (this project only) | 'global' (all reports). Absent ⇒ not an
   *  override (or a legacy global doc, which the resolver treats as 'global'). */
  override_scope?: 'project' | 'global' | null;
}

/**
 * One AUO marker, as the FORM holds it (pre-persist). The zone is DERIVED from
 * the point + grid (never typed); the name comes from the palette (or inline-add).
 */
export interface AuoMark {
  /** form-local id (marker key + remove/edit handle). Stable = index `au${i}`. */
  id: string;
  /** click location in IMAGE-PIXEL space (OQ-C) — the marker anchor (region
   *  centre). Re-renders + re-derives the zone(s). */
  point: Pt;
  /** the AUO SHAPE (square/rectangle with rotation, or circle). A bed/cabinet
   *  has AREA → can straddle multiple zones. Absent on a legacy point-AUO (then
   *  treated as a degenerate square at `point`). */
  region?: AuoRegion;
  /** the FIRST derived 16-zone (= zones[0] or ''). Kept for backward
   *  compatibility with the single-zone persist/callable path + the on-map label. */
  zone: string;
  /** DERIVED set of 16-zones the shape overlaps, compass order (e.g.
   *  ['NE','ENE']). '' / [] when the shape fell off-grid. The multi-zone signal. */
  zones: string[];
  /** the AUO name (palette label or inline-added). */
  name: string;
  /** Activity | Utility | Object (label only). */
  type: AuoType;
  /** review status — proposed (default on mark) | confirmed | rejected. */
  status: AuoStatus;
  /** who marked it — 'staff' (default). */
  source: 'staff' | 'engine';
  /** the engine's disposition reasoning per covered zone (keyed by zone code),
   *  once fetched. A single-zone AUO has one entry. */
  suggestionsByZone?: Record<string, AuoSuggestion>;
  /** set on reject — the training signal. */
  rejected_by?: string;
  rejected_at?: string;
}

/* ───────────────────────── point → zone (the ONE derivation) ───────────────────────── */

/**
 * PURE — the 16-zone an interior point falls in, via `zoneAtPoint` (the grid's
 * own zone wedges — same math the report uses). Returns '' when there is no grid
 * or the point is off-grid (so the mark's `zone` stays a string).
 */
export function zoneForPoint(grid: GridResult | null, pt: Pt): string {
  if (!grid) return '';
  const z = zoneAtPoint(grid, pt);
  return z ?? '';
}

/** The region a mark uses: its explicit `region`, or a degenerate square at its
 *  `point` (so a legacy point-AUO still derives a zone set of size ≤1). */
export function regionForMark(m: Pick<AuoMark, 'region' | 'point'>): AuoRegion {
  return m.region ?? defaultRegionAt(m.point);
}

/**
 * PURE — the SET of 16-zones a mark's SHAPE overlaps (compass order), via
 * `zonesForRegion` (the region case of zoneAtPoint — the SAME classifier the
 * door multi-pada coverage reuses). The single source of truth for a mark's
 * zones. Returns [] when off-grid.
 */
export function zonesForMark(grid: GridResult | null, m: Pick<AuoMark, 'region' | 'point'>): string[] {
  return zonesForRegion(grid, regionForMark(m));
}

/** Internal — set zones + the legacy first-zone on a mark, clearing the stale
 *  per-zone suggestions whose zone is no longer covered. */
function withZones(m: AuoMark, zones: string[]): AuoMark {
  const next: AuoMark = { ...m, zones, zone: zones[0] || '' };
  if (m.suggestionsByZone) {
    const kept: Record<string, AuoSuggestion> = {};
    for (const z of zones) {
      if (m.suggestionsByZone[z]) kept[z] = m.suggestionsByZone[z];
    }
    next.suggestionsByZone = Object.keys(kept).length ? kept : undefined;
  }
  return next;
}

/** A finite {x,y} or null (defensive read of a persisted/optional point). */
function finitePt(p: Pt | null | undefined): Pt | null {
  return p &&
    typeof p.x === 'number' &&
    typeof p.y === 'number' &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y)
    ? { x: p.x, y: p.y }
    : null;
}

/* ───────────────────────── mark builders / ops ───────────────────────── */

/**
 * PURE — build a fresh AUO mark for a CLICK point inside the footprint: a small
 * default SQUARE region at the click, derive its zone SET off the grid, default
 * name '' (staff picks from the palette), default type 'Object', status
 * 'proposed'. Returns null only on a non-finite point.
 */
export function makeMark(grid: GridResult | null, click: Pt): AuoMark | null {
  const pt = finitePt(click);
  if (!pt) return null;
  const point = { x: Math.round(pt.x), y: Math.round(pt.y) };
  const region = defaultRegionAt(point);
  const zones = zonesForRegion(grid, region);
  return {
    id: 'au_new',
    point,
    region,
    zone: zones[0] || '',
    zones,
    name: '',
    type: 'Object',
    status: 'proposed',
    source: 'staff',
  };
}

/** PURE — append a new AUO mark for an interior click. */
export function addMark(marks: AuoMark[], grid: GridResult | null, click: Pt): AuoMark[] {
  const m = makeMark(grid, click);
  return m ? [...marks, m] : marks;
}

/** Internal — re-derive a mark's zone set off the grid AND re-open its decision
 *  (a geometry change makes any prior disposition/confirm stale). */
function reopenWithGeometry(m: AuoMark, region: AuoRegion, grid: GridResult | null): AuoMark {
  const zones = zonesForRegion(grid, region);
  return {
    ...withZones(m, zones),
    region,
    point: { x: region.cx, y: region.cy },
    status: 'proposed' as AuoStatus,
    suggestionsByZone: undefined,
    rejected_by: undefined,
    rejected_at: undefined,
  };
}

/**
 * PURE — TRANSLATE an AUO marker so its region centre follows the drag, then
 * re-derive its zone SET off the grid (the zones must always reflect the current
 * shape — the trust signal). Moving re-opens the decision (back to 'proposed').
 */
export function moveMark(
  marks: AuoMark[],
  id: string,
  drag: Pt,
  grid: GridResult | null,
): AuoMark[] {
  const pt = finitePt(drag);
  if (!pt) return marks;
  return marks.map((m) => {
    if (m.id !== id) return m;
    const cx = Math.round(pt.x);
    const cy = Math.round(pt.y);
    const reg = regionForMark(m);
    const moved: AuoRegion = { ...reg, cx, cy };
    return reopenWithGeometry(m, moved, grid);
  });
}

/**
 * PURE — RESIZE an AUO's shape (a rect's half-extents `hw`,`hh`, or a circle's
 * radius `r`), keeping its centre + rotation, then re-derive its zone SET. Used
 * by the corner/edge resize grips. Re-opens the decision.
 *
 * NOTE — this is the CENTRE-anchored resize (all sides stretch from the centre).
 * The interactive grip drag uses `resizeMarkAnchored` below (opposite corner
 * PINNED — the standard bounding-box behaviour). This setter is retained for
 * non-interactive / programmatic sizing and for the circle case.
 */
export function resizeMark(
  marks: AuoMark[],
  id: string,
  size: { hw?: number; hh?: number; r?: number },
  grid: GridResult | null,
): AuoMark[] {
  return marks.map((m) => {
    if (m.id !== id) return m;
    const reg = regionForMark(m);
    let next: AuoRegion;
    if (reg.kind === 'circle') {
      const r = Math.max(4, Math.round(size.r ?? reg.r));
      next = { ...reg, r };
    } else {
      const hw = Math.max(4, Math.round(size.hw ?? reg.hw));
      const hh = Math.max(4, Math.round(size.hh ?? reg.hh));
      next = { ...reg, hw, hh };
    }
    return reopenWithGeometry(m, next, grid);
  });
}

/**
 * PURE — GRIP-ANCHORED resize: drag a corner grip and the OPPOSITE corner stays
 * PINNED while the shape resizes toward the grip (standard bounding-box resize,
 * NOT the centre-stretch of `resizeMark`). The drag point is in IMAGE-PIXEL space;
 * `gripCorner` is which corner is being dragged (0=TL,1=TR,2=BR,3=BL, clockwise —
 * matching `rectCorners`). The new rect is reconstructed so the corner DIAGONALLY
 * OPPOSITE the grip is unchanged; both the centre and the half-extents move. For a
 * CIRCLE the grip sits at the right edge → its OPPOSITE edge (left) is pinned, so
 * the new radius = half the (pinned-edge → drag) span and the centre shifts to the
 * midpoint. Rotation is preserved. Re-derives zones + re-opens the decision.
 *
 * Math (rect): work in the rect's LOCAL (un-rotated) frame.
 *   pinnedLocal = the opposite corner's local coords (±hw, ±hh) — FIXED.
 *   dragLocal   = un-rotate (drag − centre) into local, giving the grip's new
 *                 local position. The new local box spans [pinnedLocal, dragLocal];
 *                 hw' = |dragLocal.x − pinnedLocal.x| / 2, hh' similarly, and the
 *                 new local centre = midpoint. Rotate the new local centre back to
 *                 image space to get cx',cy' (the pinned corner stays put because
 *                 the box is rebuilt around it).
 */
export function resizeMarkAnchored(
  marks: AuoMark[],
  id: string,
  gripCorner: number,
  drag: Pt,
  grid: GridResult | null,
): AuoMark[] {
  const pt = finitePt(drag);
  if (!pt) return marks;
  return marks.map((m) => {
    if (m.id !== id) return m;
    const reg = regionForMark(m);
    if (reg.kind === 'circle') {
      // grip at the right edge (cx+r, cy); pinned edge at the left (cx−r, cy).
      const pinned = { x: reg.cx - reg.r, y: reg.cy };
      const r = Math.max(4, Math.round(Math.hypot(pt.x - pinned.x, pt.y - pinned.y) / 2));
      const cx = Math.round((pinned.x + pt.x) / 2);
      const cy = Math.round((pinned.y + pt.y) / 2);
      return reopenWithGeometry(m, { kind: 'circle', cx, cy, r }, grid);
    }
    const rad = (reg.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    // local coords of each corner (clockwise from TL), parity with rectCorners().
    const cornersLocal: Pt[] = [
      { x: -reg.hw, y: -reg.hh },
      { x: reg.hw, y: -reg.hh },
      { x: reg.hw, y: reg.hh },
      { x: -reg.hw, y: reg.hh },
    ];
    const gi = ((gripCorner % 4) + 4) % 4;
    const opp = (gi + 2) % 4; // the diagonally-opposite corner (the pinned one).
    const pinnedLocal = cornersLocal[opp];
    // un-rotate (drag − centre) into the rect's local frame → the grip's new local pos.
    const dx = pt.x - reg.cx;
    const dy = pt.y - reg.cy;
    const gripLocalX = dx * cos + dy * sin; // inverse rotation (−θ)
    const gripLocalY = -dx * sin + dy * cos;
    const hw = Math.max(4, Math.round(Math.abs(gripLocalX - pinnedLocal.x) / 2));
    const hh = Math.max(4, Math.round(Math.abs(gripLocalY - pinnedLocal.y) / 2));
    // new local centre = midpoint of the pinned corner and the dragged grip.
    const midLocalX = (pinnedLocal.x + gripLocalX) / 2;
    const midLocalY = (pinnedLocal.y + gripLocalY) / 2;
    // rotate the new local centre back to image space (relative to the OLD centre).
    const cx = Math.round(reg.cx + midLocalX * cos - midLocalY * sin);
    const cy = Math.round(reg.cy + midLocalX * sin + midLocalY * cos);
    return reopenWithGeometry(m, { kind: 'rect', cx, cy, hw, hh, rotation: reg.rotation }, grid);
  });
}

/**
 * PURE — ROTATE a rect AUO (deg, clockwise), re-derive its zone SET. A circle is
 * rotation-invariant → no-op. Re-opens the decision.
 */
export function rotateMark(
  marks: AuoMark[],
  id: string,
  rotationDeg: number,
  grid: GridResult | null,
): AuoMark[] {
  return marks.map((m) => {
    if (m.id !== id) return m;
    const reg = regionForMark(m);
    if (reg.kind !== 'rect') return m;
    const rotation = ((rotationDeg % 360) + 360) % 360;
    return reopenWithGeometry(m, { ...reg, rotation }, grid);
  });
}

/**
 * PURE — switch an AUO's shape KIND (rect ⇆ circle), preserving centre + size,
 * re-derive zones. Re-opens the decision.
 */
export function setMarkShape(
  marks: AuoMark[],
  id: string,
  kind: 'rect' | 'circle',
  grid: GridResult | null,
): AuoMark[] {
  return marks.map((m) => {
    if (m.id !== id) return m;
    const reg = regionForMark(m);
    if (reg.kind === kind) return m;
    let next: AuoRegion;
    if (kind === 'circle') {
      const r = reg.kind === 'rect' ? Math.round(Math.max(reg.hw, reg.hh)) : 40;
      next = { kind: 'circle', cx: reg.cx, cy: reg.cy, r };
    } else {
      const half = reg.kind === 'circle' ? reg.r : 40;
      next = { kind: 'rect', cx: reg.cx, cy: reg.cy, hw: half, hh: half, rotation: 0 };
    }
    return reopenWithGeometry(m, next, grid);
  });
}

/** PURE — remove an AUO mark by id. */
export function removeMark(marks: AuoMark[], id: string): AuoMark[] {
  return marks.filter((m) => m.id !== id);
}

/** PURE — set an AUO's name (palette pick or inline-add). Re-marks it proposed
 *  + clears the stale suggestions (the name changed → the dispositions must be
 *  re-fetched before it can be confirmed). */
export function setMarkName(marks: AuoMark[], id: string, name: string): AuoMark[] {
  return marks.map((m) =>
    m.id === id
      ? { ...m, name, status: 'proposed' as AuoStatus, suggestionsByZone: undefined }
      : m,
  );
}

/** PURE — set an AUO's type (Activity/Utility/Object). A label only — does NOT
 *  re-open the decision (type never affects the disposition). */
export function setMarkType(marks: AuoMark[], id: string, type: string): AuoMark[] {
  if (!isAuoType(type)) return marks;
  return marks.map((m) => (m.id === id ? { ...m, type } : m));
}

/**
 * PURE — manually OVERRIDE an AUO's zone SET (the "Edit zones" affordance).
 * Normally the zones are derived from the shape; this lets staff correct an edge
 * case without re-drawing. Re-marks it proposed + clears the suggestions.
 * Accepts a single code or a list (deduped, upper-cased, blanks dropped).
 */
export function setMarkZones(marks: AuoMark[], id: string, zones: string | string[]): AuoMark[] {
  const list = (Array.isArray(zones) ? zones : [zones])
    .map((z) => (z || '').trim().toUpperCase())
    .filter(Boolean);
  const uniq = Array.from(new Set(list));
  return marks.map((m) =>
    m.id === id
      ? { ...withZones(m, uniq), status: 'proposed' as AuoStatus, suggestionsByZone: undefined }
      : m,
  );
}

/** PURE — attach ONE engine suggestion (keyed by its zone) to a mark. Does NOT
 *  change status — the staff still confirm/reject. Matched by id. A multi-zone
 *  AUO accumulates one entry per zone. */
export function applySuggestion(
  marks: AuoMark[],
  id: string,
  suggestion: AuoSuggestion,
): AuoMark[] {
  return marks.map((m) => {
    if (m.id !== id) return m;
    const z = (suggestion.zone || m.zone || '').toUpperCase();
    const byZone = { ...(m.suggestionsByZone || {}), [z]: { ...suggestion, zone: z } };
    return { ...m, suggestionsByZone: byZone };
  });
}

/** PURE — attach a WHOLE per-zone suggestion map to a mark in one patch (the
 *  callable returns all zones at once). Matched by id. */
export function applySuggestions(
  marks: AuoMark[],
  id: string,
  byZone: Record<string, AuoSuggestion>,
): AuoMark[] {
  return marks.map((m) => (m.id === id ? { ...m, suggestionsByZone: byZone } : m));
}

/** PURE — CONFIRM an AUO (staff accepts it → reaches compose). Clears any prior
 *  rejection metadata. */
export function confirmMark(marks: AuoMark[], id: string): AuoMark[] {
  return marks.map((m) =>
    m.id === id
      ? { ...m, status: 'confirmed' as AuoStatus, rejected_by: undefined, rejected_at: undefined }
      : m,
  );
}

/** PURE — RE-OPEN a confirmed (or rejected) AUO for EDITING. Sets the status back
 *  to 'proposed' so the geometry / name / zones / disposition can be changed and
 *  re-confirmed, WITHOUT discarding the engine suggestions already attached (the
 *  rich per-zone rulings stay visible while the staff edits — unlike a geometry
 *  change, which legitimately invalidates them). Clears any rejection metadata.
 *  This is the "Edit" affordance on a confirmed card / marker. A no-op on a mark
 *  already 'proposed'. */
export function reopenMark(marks: AuoMark[], id: string): AuoMark[] {
  return marks.map((m) =>
    m.id === id && m.status !== 'proposed'
      ? { ...m, status: 'proposed' as AuoStatus, rejected_by: undefined, rejected_at: undefined }
      : m,
  );
}

/** PURE — REJECT an AUO (logged, NOT compiled). Stamps rejected_by/at (training
 *  signal). `at` defaults to now() but is injectable for deterministic tests. */
export function rejectMark(
  marks: AuoMark[],
  id: string,
  by: string,
  at: string = new Date().toISOString(),
): AuoMark[] {
  return marks.map((m) =>
    m.id === id
      ? { ...m, status: 'rejected' as AuoStatus, rejected_by: by || 'staff', rejected_at: at }
      : m,
  );
}

/** Stable string for a zone set, for the "did it change" comparison. */
function zonesKey(zones: string[]): string {
  return zones.slice().sort().join(',');
}

/**
 * PURE — re-derive EVERY mark's zone SET off a (new) grid (the boundary or north
 * changed → the grid moved, so each shape's zones must be recomputed from its
 * unchanged region). A mark whose shape is now off-grid gets zones []. A
 * re-derive that CHANGES a mark's zone set re-opens it (proposed) so a stale
 * confirmation never reaches compose with the wrong zones.
 */
export function rederiveMarks(marks: AuoMark[], grid: GridResult | null): AuoMark[] {
  return marks.map((m) => {
    const zones = zonesForMark(grid, m);
    if (zonesKey(zones) === zonesKey(m.zones || [])) return m;
    // the zones moved under the shape → the prior decision is stale.
    return {
      ...withZones(m, zones),
      status: 'proposed' as AuoStatus,
      suggestionsByZone: undefined,
      rejected_by: undefined,
      rejected_at: undefined,
    };
  });
}

/* ───────────────────────── derived counts (UI attention) ───────────────────────── */

/** PURE — counts for the review header: how many proposed / confirmed / rejected
 *  / unrecognised (name won't normalise → would be silently skipped). */
export function reviewCounts(marks: AuoMark[]): {
  total: number;
  proposed: number;
  confirmed: number;
  rejected: number;
  unrecognised: number;
} {
  let proposed = 0,
    confirmed = 0,
    rejected = 0,
    unrecognised = 0;
  for (const m of marks) {
    if (m.status === 'confirmed') confirmed++;
    else if (m.status === 'rejected') rejected++;
    else proposed++;
    // unrecognised if ANY of its per-zone suggestions came back not-recognised.
    const sugs = m.suggestionsByZone ? Object.values(m.suggestionsByZone) : [];
    if (sugs.some((s) => s.recognised === false)) unrecognised++;
  }
  return { total: marks.length, proposed, confirmed, rejected, unrecognised };
}

/** PURE — the marks the engine still NEEDS the staff to act on (proposed, with at
 *  least one placeable zone + a name). A non-zero count = "pending review" (never
 *  silently omitted — see the hard-rule enforcement). */
export function pendingReviewCount(marks: AuoMark[]): number {
  return marks.filter(
    (m) => m.status === 'proposed' && (m.zones || []).length > 0 && !!(m.name || '').trim(),
  ).length;
}

/* ───────────────────────── persist mapping ───────────────────────── */

/** PURE — one mark → its persisted `fixtures[]` entry.
 *
 *  ⭐ BUG FIX (2026-06-08): a FRESHLY-PLACED AUO is intentionally NAMELESS
 *  (`name: ''`) until the staff picks from the palette in the review card. The
 *  OLD guard `if (!name || !zone) return null` dropped that nameless mark — so
 *  `applyAuoMarks` wrote an empty `fixtures: []`, and the very next render
 *  re-read ZERO marks via `auoMarksFor`: the marker VANISHED the instant it was
 *  placed (owner: "click inside the plot, nothing happens"). The door path never
 *  had this because `marksToFloorPatch` maps EVERY door (no name gate).
 *
 *  The "needs a name to be analysed" rule belongs ONLY at (a) the Confirm gate
 *  (the button is disabled while `!name`) and (b) the server compose seam (which
 *  strips `status !== 'confirmed'` — a nameless mark is always 'proposed', never
 *  confirmed, so it can never reach the engine). So the FORM projection must
 *  KEEP a placeable marker: a mark with a valid POINT is a renderable
 *  work-in-progress fixture to be named, even with an empty name/zone (off-grid
 *  shows as "off-grid ✎"). We still drop a degenerate mark with NEITHER a point
 *  NOR a name+zone (nothing to render and nothing to analyse).
 */
export function markToFixture(m: AuoMark): FixtureInput | null {
  const name = (m.name || '').trim();
  const zones = (m.zones || []).map((z) => (z || '').trim().toUpperCase()).filter(Boolean);
  const zone = (m.zone || zones[0] || '').trim().toUpperCase();
  const pt = finitePt(m.point);
  // keep a renderable marker (has a point) OR an analysable fixture (name+zone).
  if (!pt && (!name || (!zone && zones.length === 0))) return null;
  const out: FixtureInput = {
    name,
    // `zone` (singular) = the first covered zone — kept for the single-zone
    // compose/callable path; `zones` carries the full multi-zone set.
    zone: zone || zones[0] || '',
    zones,
    type: m.type || 'Object',
    status: m.status,
    source: m.source || 'staff',
    point: { x: Math.round(m.point.x), y: Math.round(m.point.y) },
  };
  if (m.region) out.region = m.region;
  if (m.suggestionsByZone && Object.keys(m.suggestionsByZone).length) {
    out.suggestionsByZone = m.suggestionsByZone;
  }
  if (m.status === 'rejected') {
    if (m.rejected_by) out.rejected_by = m.rejected_by;
    if (m.rejected_at) out.rejected_at = m.rejected_at;
  }
  return out;
}

/**
 * PURE — the marks of one floor → the floor's persisted `fixtures[]` patch. Keeps
 * ALL statuses (proposed/confirmed/rejected) so the staff review list round-trips
 * fully on reload; the COMPOSE SEAM (server) strips non-confirmed before the
 * engine. De-dup is NOT applied (two Toilets in different zones are distinct).
 */
export function marksToFixtures(marks: AuoMark[]): FixtureInput[] {
  return marks
    .map(markToFixture)
    .filter((x): x is FixtureInput => x != null);
}

/**
 * PURE — hydrate the AUO marks for one floor from its PERSISTED `fixtures[]` and
 * RE-DERIVE each zone off the supplied grid (so the on-map zone always reflects
 * the current grid). STABLE id = the fixture's index (`au${i}`), the same
 * derive-on-render / one-owner pattern as entranceMarks. A fixture missing a
 * point cannot be re-rendered on the map but is NOT destroyed here — it is kept
 * as a mark at its stored point (or skipped if no point); `marksToFixtures`
 * re-emits it on the next save.
 */
export function marksFromFloor(floor: FloorInput, grid: GridResult | null): AuoMark[] {
  const src = Array.isArray(floor.fixtures) ? floor.fixtures : [];
  const out: AuoMark[] = [];
  for (let i = 0; i < src.length; i++) {
    const fx = src[i] || ({} as FixtureInput);
    const pt = finitePt(fx.point);
    if (!pt) continue; // no point → can't render a marker (legacy bucket-AUO etc.)
    const status: AuoStatus = isAuoStatus(fx.status) ? fx.status : 'confirmed';
    // The region: a persisted shape, OR a degenerate square at the point (legacy
    // single-point AUO → upgraded to a tiny rect so multi-zone derivation works).
    const region = sanitiseRegion(fx.region) ?? defaultRegionAt(pt);
    // RE-DERIVE the zone SET from the shape + current grid (the trust signal).
    // Fall back to the stored zones/zone only when off the current grid.
    const derived = zonesForRegion(grid, region);
    const storedZones = Array.isArray(fx.zones)
      ? fx.zones.map((z) => (typeof z === 'string' ? z.toUpperCase() : '')).filter(Boolean)
      : [];
    const storedZone = typeof fx.zone === 'string' ? fx.zone.toUpperCase() : '';
    const zones =
      derived.length > 0
        ? derived
        : storedZones.length
          ? storedZones
          : storedZone
            ? [storedZone]
            : [];
    out.push({
      id: `au${i}`,
      point: pt,
      region,
      zone: zones[0] || '',
      zones,
      name: typeof fx.name === 'string' ? fx.name : '',
      type: isAuoType(fx.type) ? fx.type : 'Object',
      status,
      source: fx.source === 'engine' ? 'engine' : 'staff',
      suggestionsByZone: sanitiseSuggestions(fx.suggestionsByZone),
      rejected_by: typeof fx.rejected_by === 'string' ? fx.rejected_by : undefined,
      rejected_at: typeof fx.rejected_at === 'string' ? fx.rejected_at : undefined,
    });
  }
  return out;
}

/** Defensive read of a persisted region (rect/circle) → a valid AuoRegion or
 *  undefined. */
function sanitiseRegion(r: unknown): AuoRegion | undefined {
  if (!r || typeof r !== 'object') return undefined;
  const o = r as Record<string, unknown>;
  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;
  const cx = num(o.cx);
  const cy = num(o.cy);
  if (cx == null || cy == null) return undefined;
  if (o.kind === 'circle') {
    const rad = num(o.r);
    if (rad == null || rad <= 0) return undefined;
    return { kind: 'circle', cx, cy, r: rad };
  }
  // default to rect
  const hw = num(o.hw);
  const hh = num(o.hh);
  if (hw == null || hh == null || hw <= 0 || hh <= 0) return undefined;
  const rotation = num(o.rotation) ?? 0;
  return { kind: 'rect', cx, cy, hw, hh, rotation };
}

/** Defensive read of a persisted per-zone suggestion map. */
function sanitiseSuggestions(s: unknown): Record<string, AuoSuggestion> | undefined {
  if (!s || typeof s !== 'object') return undefined;
  const out: Record<string, AuoSuggestion> = {};
  for (const [k, v] of Object.entries(s as Record<string, unknown>)) {
    if (v && typeof v === 'object') out[k.toUpperCase()] = v as AuoSuggestion;
  }
  return Object.keys(out).length ? out : undefined;
}
